/**
 * Where each card of a node-link diagram goes.
 *
 * Deliberately *not* in `packages/domain/`: a pixel is not a domain concept,
 * and `structure.ts` has to stay the answer to "what is this made of"
 * regardless of whether anybody draws it. It is still a pure function with its
 * own test, for the same reason everything load-bearing here is — an
 * off-by-one in a layout is invisible in a screenshot and obvious in an
 * assertion.
 *
 * The algorithm is the simple half of Reingold–Tilford, which is all a bill of
 * materials needs: walk depth-first, give every leaf the next column, and
 * centre every parent over its own children. Subtrees are laid out one after
 * another and a parent never leaves its children's span, so nothing can
 * overlap and there is no contour to thread. Full tidy-tree machinery buys
 * compactness for deep sparse trees; a BOM is wide and shallow.
 *
 * Growing `down`, `y` is depth: the root is at the top and the parts that are
 * made of nothing are along the bottom. Growing `right`, `x` is depth — the
 * shape "How it's made" uses (see `orientation`).
 */

/** Anything with a stable key and children. `DesignNode` is one. */
export interface Layoutable<T> {
  readonly key: string
  readonly children: readonly T[]
}

export interface LayoutOptions {
  readonly cardW: number
  readonly cardH: number
  /** Space between two sibling cards — horizontal when the tree grows down,
   *  vertical when it grows right. */
  readonly colGap: number
  /** Space between one level and the next — where the connectors live. */
  readonly rowGap: number
  /**
   * Which way the tree grows. `down` puts the root at the top; `right` puts it
   * at the left, which is the better shape for a bill of materials: a BOM is
   * shallow and wide, so its breadth goes down the screen (which scrolls) and
   * its few levels go across (which does not), and the cards can be wide enough
   * to hold a name, a picture and a stock count side by side.
   */
  readonly orientation?: 'down' | 'right'
}

export const DEFAULT_LAYOUT: LayoutOptions = {
  cardW: 148,
  cardH: 132,
  colGap: 20,
  rowGap: 60,
}

export interface Placed<T> {
  readonly node: T
  /** Top-left of the card, in diagram coordinates. */
  readonly x: number
  readonly y: number
  readonly depth: number
}

export interface Edge {
  readonly key: string
  /** Where the connector leaves the parent: bottom-centre growing down,
   *  right-middle growing right. */
  readonly x1: number
  readonly y1: number
  /** Where it reaches the child: top-centre, or left-middle. */
  readonly x2: number
  readonly y2: number
}

export interface Layout<T> {
  readonly nodes: readonly Placed<T>[]
  readonly edges: readonly Edge[]
  readonly width: number
  readonly height: number
}

export function layoutTree<T extends Layoutable<T>>(
  root: T,
  options: LayoutOptions = DEFAULT_LAYOUT,
): Layout<T> {
  const { cardW, cardH, colGap, rowGap } = options
  const right = options.orientation === 'right'
  /* Lay out in "down" terms — breadth across, depth along — and transpose at
     the end for `right`. One algorithm, so the no-overlap proof covers both. */
  const breadth = right ? cardH : cardW
  const length = right ? cardW : cardH
  const step = breadth + colGap
  const placedDown: { node: T; across: number; along: number; depth: number }[] = []
  const links: { key: string; parent: number; child: number; along: number }[] = []
  let nextColumn = 0

  /** Returns the card's centre on the breadth axis, so the parent can sit
   *  between its children. */
  const place = (node: T, depth: number): number => {
    const along = depth * (length + rowGap)
    let centre: number

    if (node.children.length === 0) {
      centre = nextColumn * step + breadth / 2
      nextColumn += 1
    } else {
      const childCentres = node.children.map((c) => place(c, depth + 1))
      const first = childCentres[0] ?? 0
      const last = childCentres[childCentres.length - 1] ?? 0
      centre = (first + last) / 2
      for (const [i, childCentre] of childCentres.entries()) {
        const child = node.children[i]
        if (child === undefined) continue
        links.push({ key: child.key, parent: centre, child: childCentre, along })
      }
    }

    placedDown.push({ node, across: centre - breadth / 2, along, depth })
    return centre
  }

  place(root, 0)

  const depth = placedDown.reduce((d, n) => Math.max(d, n.depth), 0)
  const extentAcross = Math.max(nextColumn, 1) * step - colGap
  const extentAlong = (depth + 1) * length + depth * rowGap
  return {
    // Parents before children: the order a reader expects, and the order the
    // DOM should be in for a screen reader walking the diagram.
    nodes: [...placedDown].reverse().map((p) => ({
      node: p.node,
      x: right ? p.along : p.across,
      y: right ? p.across : p.along,
      depth: p.depth,
    })),
    edges: links.map((l) =>
      right
        ? { key: l.key, x1: l.along + length, y1: l.parent, x2: l.along + length + rowGap, y2: l.child }
        : { key: l.key, x1: l.parent, y1: l.along + length, x2: l.child, y2: l.along + length + rowGap },
    ),
    width: right ? extentAlong : extentAcross,
    height: right ? extentAcross : extentAlong,
  }
}

/**
 * The view a diagram opens on.
 *
 * Fit, when fitting leaves it readable. When it does not — a real robot is a
 * few hundred lines — a fitted overview is a picture of confetti, so instead
 * open at a scale where the cards can be read with the root on screen, near
 * the left edge and centred vertically on it: the place a person starts
 * reading. `Fit` is one button away for the shape of the whole thing.
 */
export function openingView(
  layout: { readonly width: number; readonly height: number },
  viewport: { readonly width: number; readonly height: number },
  root: { readonly x: number; readonly y: number; readonly height: number },
  readable = 0.6,
  padding = 32,
): { readonly scale: number; readonly x: number; readonly y: number } {
  const fitted = fitView(layout, viewport, padding)
  if (fitted.scale >= readable) return fitted
  const scale = clampScale(Math.max(readable, 0.8))
  return {
    scale,
    x: padding - root.x * scale,
    y: viewport.height / 2 - (root.y + root.height / 2) * scale,
  }
}

/**
 * The smallest pan that brings a box fully into view, or the view unchanged if
 * it already is. For keyboard focus and for the node an inspector just opened
 * on: moving the camera further than needed loses the person's place.
 */
export function revealBox(
  view: { readonly scale: number; readonly x: number; readonly y: number },
  box: { readonly x: number; readonly y: number; readonly width: number; readonly height: number },
  viewport: { readonly width: number; readonly height: number },
  margin = 24,
): { readonly scale: number; readonly x: number; readonly y: number } {
  const shift = (start: number, size: number, limit: number): number => {
    const end = start + size
    if (size + margin * 2 > limit) return margin - start // too big: align its start
    if (start < margin) return margin - start
    if (end > limit - margin) return limit - margin - end
    return 0
  }
  const left = view.x + box.x * view.scale
  const top = view.y + box.y * view.scale
  return {
    scale: view.scale,
    x: view.x + shift(left, box.width * view.scale, viewport.width),
    y: view.y + shift(top, box.height * view.scale, viewport.height),
  }
}

/**
 * The zoom that fits the whole diagram in a viewport, and the offset that
 * centres it.
 *
 * Capped at 1: a three-node tree blown up to fill a desktop looks like a
 * mistake, and the cards carry photographs sized for 1:1. Floored at a
 * genuinely small number rather than at something readable, because "you
 * cannot see the whole thing" is worse than "you can see its shape and have to
 * zoom to read it" — the shape is what this screen is for.
 */
export function fitView(
  layout: { readonly width: number; readonly height: number },
  viewport: { readonly width: number; readonly height: number },
  padding = 32,
): { readonly scale: number; readonly x: number; readonly y: number } {
  const usableW = Math.max(viewport.width - padding * 2, 1)
  const usableH = Math.max(viewport.height - padding * 2, 1)
  const scale = clampScale(
    Math.min(usableW / Math.max(layout.width, 1), usableH / Math.max(layout.height, 1), 1),
  )
  return {
    scale,
    x: (viewport.width - layout.width * scale) / 2,
    y: (viewport.height - layout.height * scale) / 2,
  }
}

export const MIN_SCALE = 0.15
export const MAX_SCALE = 2.5

export function clampScale(scale: number): number {
  if (!Number.isFinite(scale)) return 1
  return Math.min(Math.max(scale, MIN_SCALE), MAX_SCALE)
}

/**
 * Zoom about a point that stays put — the pointer under a wheel, or the middle
 * of a pinch.
 *
 * Without this the diagram slides out from under the finger doing the zooming,
 * which reads as the view being broken rather than as the zoom being naive.
 */
export function zoomAbout(
  view: { readonly scale: number; readonly x: number; readonly y: number },
  factor: number,
  origin: { readonly x: number; readonly y: number },
): { readonly scale: number; readonly x: number; readonly y: number } {
  const scale = clampScale(view.scale * factor)
  const applied = scale / view.scale
  return {
    scale,
    x: origin.x - (origin.x - view.x) * applied,
    y: origin.y - (origin.y - view.y) * applied,
  }
}
